export type ApiError = {
  error: string;
  /** Same value as `error` for domain errors (matching API). */
  code?: string;
  message: string;
  details?: Record<string, unknown>;
};

export const API_ERROR_CODES = {
  TIME_CONFLICT: 'TIME_CONFLICT',
  FORBIDDEN: 'FORBIDDEN',
  MATCHING_REQUEST_ALREADY_EXISTS: 'MATCHING_REQUEST_ALREADY_EXISTS',
  MATCHING_OVERLAP: 'MATCHING_OVERLAP',
  MATCHING_DATE_INVALID: 'MATCHING_DATE_INVALID',
  MATCHING_WINDOW_INVALID: 'MATCHING_WINDOW_INVALID',
  MATCHING_PREVIEW_EXPIRED: 'MATCHING_PREVIEW_EXPIRED',
  MATCHING_DISABLED: 'MATCHING_DISABLED',
  VERSION_MISMATCH: 'VERSION_MISMATCH',
  PRECONDITION_REQUIRED: 'PRECONDITION_REQUIRED',
} as const;

export type Role = 'user' | 'organizer' | 'admin';

export type AuthTelegramResponse = {
  user: {
    telegramId: string;
    first_name: string;
    last_name: string;
    username?: string;
    photo_url?: string;
    role: Role;
    hasClubKey: boolean;
  };
};

/** Data passed by the Telegram Login Widget `data-onauth` callback. */
export type TelegramWidgetUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

/** One-time login request confirmed through the bot (QR / deep-link flow). */
export type LoginChallenge = {
  code: string;
  deepLink: string;
  expiresAt: string;
};

export type EventType = 'boardgames' | 'ping_pong' | 'fencing' | 'archery' | 'hiking' | 'courtyard';
export type EventVisibility = 'open' | 'private';
export type EventStatus = 'active' | 'cancelled';
export type ParticipantStatus = 'pending' | 'confirmed';
export type GroupVisibility = 'open' | 'private';

export type ParticipantDto = {
  telegramId: string;
  status?: ParticipantStatus; // Опционально при создании события
};

export type EventGameDto = {
  id: string;
  name: string;
} | null;

export type EventDto = {
  id: string;
  title: string;
  type: EventType;
  visibility: EventVisibility;
  resourceId: string;
  createdByTelegramId: string;
  maxParticipants: number;
  startAt: string;
  endAt: string;
  status: EventStatus;
  updatedAt: string;
  participants: ParticipantDto[];
  game: EventGameDto;
  description: string | null;
};

export type EventCreateRequest = {
  title: string;
  type: EventType;
  resourceId: string;
  startAt: string;
  endAt: string;
  maxParticipants: number;
  visibility: EventVisibility;
  participants?: ParticipantDto[];
  gameId?: string;
  description?: string | null;
};

export type EventPatchDto = {
  id: string;
  updatedAt: string;
  status?: EventStatus;
  title?: string;
  startAt?: string;
  endAt?: string;
  maxParticipants?: number;
  visibility?: EventVisibility;
  participants?: ParticipantDto[];
};

export type EventUpdateRequest = {
  title: string;
  type: EventType;
  resourceId: string;
  startAt: string;
  endAt: string;
  maxParticipants: number;
  visibility: EventVisibility;
  description?: string | null;
};

export type EventCancelResponse = {
  id: string;
  status: EventStatus;
  updatedAt: string;
};

export type EventActionResponse = {
  event: EventPatchDto;
};

export type EventsListResponse = {
  items: EventDto[];
};

export type GroupMemberDto = {
  telegramId: string;
  status: 'pending' | 'confirmed';
};

export type GroupDto = {
  id: string;
  name: string;
  description: string;
  visibility: GroupVisibility;
  createdByTelegramId: string;
  updatedAt: string;
  members: GroupMemberDto[];
};

export type GroupsListResponse = {
  items: GroupDto[];
};

export type GroupCreateRequest = {
  name: string;
  description: string;
  visibility: GroupVisibility;
  participants?: ParticipantDto[];
};

export type GroupUpdateRequest = {
  name: string;
  description: string;
  visibility: GroupVisibility;
};

export type AdminUserDto = {
  telegramId: string;
  first_name: string;
  last_name: string;
  username?: string;
  photo_url?: string;
  role: Role;
  hasClubKey: boolean;
};

export type AdminUsersListResponse = {
  items: AdminUserDto[];
  total: number;
};

export type AdminSetRoleRequest = {
  role: Role;
};

export type AdminSetRoleResponse = {
  telegramId: string;
  role: Role;
};

// Users API
export type UserDto = {
  telegramId: string;
  firstName: string;
  lastName: string;
  username?: string;
  photoUrl?: string;
  role: Role;
  hasClubKey: boolean;
};

export type UpdateClubKeyRequest = {
  hasClubKey: boolean;
};

export type UsersListResponse = {
  items: UserDto[];
};

// Invite API
export type InviteToEventRequest = {
  telegramIds: number[];
};

export type InviteToGroupRequest = {
  telegramIds: number[];
};

// Games API
export type GameTagDto = {
  id: string;
  name: string;
};

export type GameDto = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  gameUrl: string | null;
  minPlayers: number;
  maxPlayers: number;
  estimatedDuration: number | null;
  tags: GameTagDto[];
  createdAt: string;
  updatedAt: string;
};

export type GameCreateRequest = {
  name: string;
  description?: string;
  imageUrl?: string;
  gameUrl?: string;
  minPlayers: number;
  maxPlayers: number;
  estimatedDuration?: number;
  tags: string[];
};

export type GameUpdateRequest = {
  name?: string;
  description?: string;
  imageUrl?: string;
  gameUrl?: string;
  minPlayers?: number;
  maxPlayers?: number;
  estimatedDuration?: number;
  tags?: string[];
};

export type GamesListResponse = {
  items: GameDto[];
};

export type GameImportDto = {
  name: string;
  description: string | null;
  imageUrl: string | null;
  gameUrl: string | null;
  minPlayers: number;
  maxPlayers: number;
  estimatedDuration: number | null;
  tags: string[];
};
